import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';
import { ArrowRight, ShoppingCart, LayoutDashboard, ClipboardList, FileSpreadsheet, PackageOpen } from 'lucide-react';
import HelpCenter from '../HelpCenter';
import ModuloHeader from '../ModuloHeader';
import ComprasSolicitacoes from './solicitacoes/ComprasSolicitacoes';
import ComprasCatalogo from './catalogo/ComprasCatalogo';
import ComprasReparos from './reparos/ComprasReparos';
import { perfilNoModulo } from '../../utils/perfisDerivados';

// =====================================================================
// Hub do Módulo Compras.
// Espelha o padrão dos demais hubs (Manutenção/Operacional): cards de
// sub-módulos no miolo + recorte por perfil. Paleta AMARELO #e3a81d (cor
// escolhida pela Julia, 2026-07-03 — amarelo mais fechado que o #fdbb20 da
// marca, para o texto branco ter contraste), no MESMO padrão dos demais
// módulos: texto BRANCO sobre o gradiente cor→tom mais escuro
// (#e3a81d→#d97706). Acentos de texto sobre fundo branco usam #d97706.
//
// Sub-módulos: Solicitações de Compra (peças levantadas no Diagnóstico da
// Certificação — Manutenção), Retirada de Equipamento (a fila do módulo
// LOGÍSTICA) e Catálogo da Central de Pedidos (re-upload).
// =====================================================================

const COR = '#e3a81d';

const PERMISSIONS = {
  // Compras gerencia; Manutencao (quem pediu) e Diretoria acompanham. O Gerente
  // acompanha as peças da PRÓPRIA loja (2026-08-17) — a lista já vem recortada
  // pelo backend, e os botões de ação não aparecem para ele (podeGerir, em
  // ComprasSolicitacoes.js). O catálogo abaixo NÃO o inclui: é a tabela de
  // preços da central, que não tem recorte de loja nem serve ao acompanhamento.
  'solicitacoes': ['Compras', 'Admin', 'Manutencao', 'Diretoria', 'Gerente'],
  // Catálogo: Compras, Admin e — desde 2026-08-17 — a MANUTENÇÃO, que passou a
  // cadastrar peça (é ela quem descobre a peça faltando no Diagnóstico). Tem as
  // mesmas ações de Compras aqui dentro; o que segue fora do alcance dela é
  // marcar Comprada/Entregue na aba de Solicitações (backend: PERFIS_GESTAO ×
  // PERFIS_GESTAO_CATALOGO, em server-compras.js). O Gerente continua fora: o
  // catálogo é a tabela de preços da central, sem recorte de loja possível.
  'catalogo': ['Compras', 'Admin', 'Manutencao'],
  // Retirada de Equipamento (a fila do módulo LOGÍSTICA). Aqui correm os dois
  // tipos de item: o EQUIPAMENTO retirado pela T.I e a PEÇA pedida pela
  // Manutenção — mesma esteira, mesma tela, e o selo de equipe separa.
  // ⚠️ FILA SEPARADA da de Solicitações de Compra, de propósito: aquela nasce de
  // relatório de certificação e tira loja/equipamento de três JOIN com ele.
  // ⚠️ O `Fiscal` entra AQUI (2026-09-03) porque um dos passos do fluxo é dele:
  // a nota fiscal (nº + os dois anexos). Ele age NESTA tela e só vê botão nos
  // itens que estão esperando por ele — quem decide isso é a máquina de estados
  // no backend (`retirada-fluxo.js`), não esta lista.
  // ⚠️ `Gerente TI`/`TI` entraram em 10/09: é deles a metade das retiradas, e
  // eles LEEM a fila (quem move é Compras/Fiscal).
  // ⚠️⚠️ A lista tem de caber no `PERFIS_FILA_LEITURA` do
  //    `server/logistica/server-logistica.js` — perfil aqui e fora de lá dá card
  //    que ABRE e devolve 403 em tudo. Há teste comparando os dois.
  // ⚠️ A CHAVE continua 'reparos': renomeá-la quebraria o `?tab=` de quem tem a
  //    tela nos favoritos, e não compra nada. O de-para fica aqui.
  'reparos': ['Compras', 'Admin', 'Fiscal', 'Manutencao', 'Diretoria', 'Gerente', 'Gerente TI', 'TI', 'Logistica'],
};

const modulesList = [
  {
    id: 'solicitacoes',
    label: 'Solicitações de Compra',
    icon: ClipboardList,
    color: '#d97706',
    bgColor: '#fef3c7',
    desc: 'Controle peça a peça das compras levantadas nos diagnósticos: nº do pedido no B1, compra, prazo e entrega',
    stat: 'Compras de Peças',
  },
  {
    id: 'reparos',
    label: 'Retirada de equipamento',
    icon: PackageOpen,
    color: '#d97706',
    bgColor: '#fef3c7',
    desc: 'O que saiu das lojas e do escritório — nº da solicitação no SAP, nota fiscal, envio ao fornecedor, prazo e retorno',
    stat: 'T.I e Manutenção',
  },
  {
    id: 'catalogo',
    label: 'Catálogo Central de Pedidos',
    icon: FileSpreadsheet,
    color: '#d97706',
    bgColor: '#fef3c7',
    desc: 'Peças compráveis pela Central de Pedidos — atualização por planilha (xlsx)',
    stat: 'Catálogo de Peças',
  },
];

const ComprasPainel = () => {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : {};
  const userRole = perfilNoModulo(user?.perfil, 'compras') || '';

  const [isMobile] = useState(window.innerWidth <= 768);
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const allowedModules = modulesList.filter(mod => PERMISSIONS[mod.id]?.includes(userRole) || userRole === 'Admin');

  const OverviewWidgets = () => (
    <div>
      <div style={{
        background: 'linear-gradient(135deg, #e3a81d 0%, #d97706 100%)',
        borderRadius: '16px', padding: '40px', color: '#fff',
        marginBottom: '32px', position: 'relative', overflow: 'hidden',
        boxShadow: '0 10px 15px -3px rgba(227, 168, 29, 0.45)',
      }}>
        <div style={{ position: 'relative', zIndex: 10 }}>
          <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px', marginTop: 0, color: '#fff' }}>
            Compras
          </h2>
          <p style={{ color: '#fffbeb', fontSize: '18px', maxWidth: '650px', margin: 0, lineHeight: '1.6' }}>
            Solicitações de compra de peças e catálogo da Central de Pedidos do Grupo Correia MC.
          </p>
        </div>
        <div style={{ position: 'absolute', right: '-30px', top: '-40px', opacity: 0.12 }}>
          <ShoppingCart size={250} color="#ffffff" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
        {allowedModules.map((mod) => (
          <div
            key={mod.id}
            onClick={() => setActiveTab(mod.id)}
            style={{
              backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
              border: '1px solid #fef3c7', display: 'flex', flexDirection: 'column',
              height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden',
              transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px -2px rgba(0,0,0,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)'; }}
          >
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: mod.color }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: mod.color, marginBottom: '8px', margin: 0 }}>{mod.stat}</p>
                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{mod.label}</h3>
              </div>
              <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: mod.bgColor, color: mod.color }}>
                <mod.icon size={26} />
              </div>
            </div>
            <div style={{ marginTop: 'auto' }}>
              <p style={{ fontSize: '14px', color: '#64748b', fontWeight: '500', lineHeight: '1.5', margin: '0 0 16px 0' }}>{mod.desc}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: mod.color }}>
                <span>Acessar</span> <ArrowRight size={16} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (activeTab !== 'dashboard' && !(PERMISSIONS[activeTab]?.includes(userRole) || userRole === 'Admin')) {
      setActiveTab('dashboard');
    }
  }, [activeTab, userRole]);

  const contentBoxStyle = {
    backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #fef3c7',
    padding: '32px', minHeight: '75vh', width: '100%',
  };

  const renderContent = () => {
    if (activeTab === 'dashboard') return <OverviewWidgets />;
    switch (activeTab) {
      case 'solicitacoes': return <div style={contentBoxStyle}><ComprasSolicitacoes /></div>;
      case 'reparos': return <div style={contentBoxStyle}><ComprasReparos /></div>;
      case 'catalogo': return <div style={contentBoxStyle}><ComprasCatalogo /></div>;
      default: return null;
    }
  };

  const getMarginLeft = () => window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px');

  // Por qual verbete o Manual da Tela abre — o da aba ativa (o manual mostra o
  // módulo inteiro; isto só escolhe qual já vem expandido).
  const getSecaoAjuda = () => {
    switch (activeTab) {
      case 'solicitacoes': return 'compras-solicitacoes';
      // Sem verbete próprio no Manual da Tela ainda — abre no panorama.
      case 'catalogo': return 'compras-catalogo';
      default: return 'visao-geral-compras';
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f8f8' }}>
      <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
      <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

        <ModuloHeader
          eyebrow="Módulo"
          titulo="Compras"
          cor={COR}
          onAbrirManual={() => setIsHelpOpen(true)}
          onAbrirMenu={() => setIsCollapsed(false)}
          isMobile={isMobile}
        />

        <div style={{ padding: '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

          {/* BARRA DE NAVEGAÇÃO */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '32px',
            backgroundColor: '#fff', padding: '8px', borderRadius: '12px',
            border: '1px solid #fef3c7', width: '100%',
          }}>
            <button
              onClick={() => setActiveTab('dashboard')}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                background: activeTab === 'dashboard' ? 'linear-gradient(135deg, #e3a81d 0%, #d97706 100%)' : 'transparent',
                color: activeTab === 'dashboard' ? '#fff' : '#64748b',
                boxShadow: activeTab === 'dashboard' ? '0 4px 6px rgba(227, 168, 29, 0.4)' : 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <LayoutDashboard size={18} /> Visão Geral
            </button>

            <div style={{ minWidth: '1px', backgroundColor: '#fef3c7', margin: '0 5px' }} />

            {allowedModules.map((mod) => (
              <button
                key={mod.id}
                onClick={() => setActiveTab(mod.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 20px', borderRadius: '8px', fontWeight: '600',
                  cursor: 'pointer', transition: 'all 0.2s',
                  border: activeTab === mod.id ? `2px solid ${mod.color}` : '2px solid transparent',
                  backgroundColor: activeTab === mod.id ? mod.bgColor : 'transparent',
                  color: activeTab === mod.id ? mod.color : '#64748b',
                  whiteSpace: 'nowrap',
                }}
              >
                <mod.icon size={18} /> {mod.label}
              </button>
            ))}
          </div>

          {renderContent()}
        </div>
      </div>

      {/* Manual da Tela — verbetes do módulo (HelpCenter › manualCompras). */}
      <HelpCenter
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        modulo="compras"
        secaoInicial={getSecaoAjuda()}
      />
    </div>
  );
};

export default ComprasPainel;
